import React, { useMemo, useState } from 'react';
import { Download, ArrowRight, ArrowUpRight, ArrowDownRight, FileText } from 'lucide-react';
import { motion } from 'framer-motion';
import { PageLayout } from '../../shared/PageLayout';
import { Card } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { StudentData } from './StudentDetailPanel';
import { GroupId } from '../../../config/systemConfig';
import { MS_PER_DAY } from '../../../config/constants';
import { MODULES } from '../../../content';
import type { QuestionState, FamilyMasteryState } from '../../../mastery';
import { getClassModuleStats } from '../../../utils/activityHelpers';
import { StudentReportCard } from '../components/StudentReportCard';
import { TeacherV2Route } from '../TeacherSidebarV2';
import { downloadCsv } from '../../../utils/csvExport';
import { InsightBar } from '../components/InsightBar';
import { getStudentInsights } from '../utils/differentiationInsights';
import { EmptyState } from '../../../components/shared/EmptyState';
import { ClassroomSwitcher } from '../components/ClassroomSwitcher';
import { DataFreshness } from '../../shared/DataFreshness';
import type { Account, Classroom } from '../../../accounts';
interface ReportsPageProps {
  students: StudentData[];
  selectedGroup: GroupId;
  getModuleMastery: (learnerId: string, moduleId: string) => number;
  onNavigate?: (route: TeacherV2Route) => void;
  onViewAssessmentResults?: () => void;
  classrooms?: Classroom[];
  selectedClassroomId?: string | null;
  onSelectClassroom?: (id: string | null) => void;
  activeAccount?: Account;
}
const PERIODS = [{
  key: 7,
  label: 'week'
}, {
  key: 30,
  label: 'maand'
}, {
  key: 90,
  label: 'trimester'
}, {
  key: 365,
  label: 'jaar'
}];
function getPeriodStats(questionStates: Record<string, QuestionState>, familyMastery: Record<string, FamilyMasteryState>, group: number, periodDays: number) {
  const now = Date.now();
  const periodMs = periodDays * MS_PER_DAY;
  const currentStart = now - periodMs;
  const previousStart = currentStart - periodMs;
  const uniqueDays = new Set<string>();
  for (const state of Object.values(questionStates || {})) {
    if (!state.history) continue;
    for (const attempt of state.history) {
      if (attempt.timestamp >= currentStart && attempt.timestamp <= now) {
        const dateStr = new Date(attempt.timestamp).toISOString().split('T')[0];
        uniqueDays.add(dateStr);
      }
    }
  }
  let currentCorrect = 0;
  let currentTotal = 0;
  let previousCorrect = 0;
  let previousTotal = 0;
  for (const [key, state] of Object.entries(familyMastery || {})) {
    if (!key.includes(`_g${group}`)) continue;
    if (!state.history) continue;
    for (const attempt of state.history) {
      if (attempt.timestamp >= currentStart && attempt.timestamp <= now) {
        currentTotal++;
        if (attempt.isCorrect) currentCorrect++;
      } else if (attempt.timestamp >= previousStart && attempt.timestamp < currentStart) {
        previousTotal++;
        if (attempt.isCorrect) previousCorrect++;
      }
    }
  }
  let trendDelta = 0;
  if (currentTotal >= 3 && previousTotal >= 3) {
    const currentAcc = currentCorrect / currentTotal;
    const previousAcc = previousCorrect / previousTotal;
    trendDelta = Math.round((currentAcc - previousAcc) * 100);
  }
  return {
    daysPracticed: uniqueDays.size,
    trendDelta
  };
}
export function ReportsPage({
  students,
  selectedGroup,
  getModuleMastery,
  classrooms = [],
  selectedClassroomId = null,
  onSelectClassroom,
  activeAccount
}: ReportsPageProps) {
  const [periodDays, setPeriodDays] = useState<number>(7);
  const [lastLoadedAt, setLastLoadedAt] = useState(() => new Date());
  const enrichedStudents = useMemo(() => {
    return students.map(student => {
      let totalMastery = 0;
      let practicedCount = 0;
      let lowestMastery = 100;
      let highestMastery = 0;
      let weakestModule = null;
      let strongestModule = null;
      MODULES.forEach(module => {
        const mastery = getModuleMastery(student.id, module.id);
        if (mastery > 0) {
          totalMastery += mastery;
          practicedCount++;
          if (mastery < lowestMastery) {
            lowestMastery = mastery;
            weakestModule = module;
          }
          if (mastery > highestMastery) {
            highestMastery = mastery;
            strongestModule = module;
          }
        }
      });
      const avgMastery = practicedCount > 0 ? Math.round(totalMastery / practicedCount) : 0;
      const {
        daysPracticed,
        trendDelta
      } = getPeriodStats(student.data.questionStates || {}, student.data.familyMastery || {}, selectedGroup, periodDays);
      return {
        ...student,
        avgMastery,
        lowestMastery,
        weakestModule,
        strongestModule,
        daysPracticed,
        trendDelta
      };
    });
  }, [students, getModuleMastery, selectedGroup, periodDays]);
  const moduleStats = useMemo(() => {
    return getClassModuleStats(students, selectedGroup, getModuleMastery);
  }, [students, selectedGroup, getModuleMastery]);
  const summaryStats = useMemo(() => {
    if (enrichedStudents.length === 0) return {
      classAvg: 0,
      activeThisPeriod: 0,
      totalPracticedModules: 0
    };
    const totalAvg = enrichedStudents.reduce((sum, s) => sum + s.avgMastery, 0);
    const classAvg = Math.round(totalAvg / enrichedStudents.length);
    const activeThisPeriod = enrichedStudents.filter(s => s.daysPracticed > 0).length;
    const totalPracticedModules = moduleStats.filter(m => m.practicedCount > 0).length;
    return {
      classAvg,
      activeThisPeriod,
      totalPracticedModules
    };
  }, [enrichedStudents, moduleStats]);
  const activePeriodLabel = PERIODS.find(p => p.key === periodDays)?.label || 'week';
  const handleExportClassListCsv = () => {
    const headers = ['Naam', 'Gemiddelde Beheersing (%)', 'Trend (%)', `Dagen Geoefend (deze ${activePeriodLabel})`, 'Sterkste Vak', 'Zwakste Vak'];
    const rows = enrichedStudents.map(s => {
      return [`"${s.name}"`, s.avgMastery, s.trendDelta, s.daysPracticed, `"${s.strongestModule?.title || ''}"`, `"${s.weakestModule?.title || ''}"`].join(',');
    });
    const csvContent = [headers.join(','), ...rows].join('\n');
    downloadCsv(csvContent, 'klassenlijst');
  };
  const handleExportSubjectsCsv = () => {
    const headers = ['Vak', 'Klasgemiddelde (%)', 'Leerlingen Geoefend', 'Trend'];
    const rows = moduleStats.map(m => {
      const trendText = m.trend === 'up' ? 'Stijgt' : m.trend === 'down' ? 'Daalt' : 'Stabiel';
      return [`"${m.moduleTitle}"`, m.avgMastery, m.practicedCount, `"${trendText}"`].join(',');
    });
    const csvContent = [headers.join(','), ...rows].join('\n');
    downloadCsv(csvContent, 'vakken_overzicht');
  };
  const handleExportFullClassReport = () => {
    const dateStr = new Date().toLocaleDateString('nl-NL', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
    const headers = ['Naam', 'Gemiddelde (%)', 'Trend (%)', `Dagen Geoefend (${activePeriodLabel})`, 'Sterkste Vak', 'Zwakste Vak', 'Aandachtspunt 1', 'Beheersing 1 (%)', 'Aanbeveling 1', 'Aandachtspunt 2', 'Beheersing 2 (%)', 'Aanbeveling 2'];
    const rows = enrichedStudents.map(s => {
      const insights = getStudentInsights(s.data, selectedGroup, 2);
      const i1 = insights[0];
      const i2 = insights[1];
      return [`"${s.name}"`, s.avgMastery, s.trendDelta, s.daysPracticed, `"${s.strongestModule?.title || ''}"`, `"${s.weakestModule?.title || ''}"`, i1 ? `"${i1.familyName}"` : '', i1 ? i1.mastery : '', i1 ? `"${i1.recommendation}"` : '', i2 ? `"${i2.familyName}"` : '', i2 ? i2.mastery : '', i2 ? `"${i2.recommendation}"` : ''].join(',');
    });
    const csvContent = [`Volledig Klasrapport — Meester Djay`, `Groep: ${selectedGroup}`, `Datum: ${dateStr}`, `Periode: Deze ${activePeriodLabel}`, `Klasgemiddelde: ${summaryStats.classAvg}%`, `Actieve leerlingen: ${summaryStats.activeThisPeriod}/${students.length}`, '', headers.join(','), ...rows].join('\n');
    downloadCsv(csvContent, `klasrapport_groep${selectedGroup}`);
  };
  const headerActions = classrooms.length > 0 && onSelectClassroom ? <ClassroomSwitcher classrooms={classrooms} selectedClassroomId={selectedClassroomId} onSelectClassroom={onSelectClassroom} activeTeacherId={activeAccount?.id} data-id="element-10469" data-mp="2jbntjox2kw8x" /> : null;
  if (students.length === 0 || summaryStats.activeThisPeriod === 0) {
    return <PageLayout title="Rapporten" description="Overzicht van de klas" actions={headerActions} data-id="element-10470" data-mp="2x43xqt1pj0bf">
        
        <div className="max-w-5xl mx-auto pb-12" data-id="element-10471" data-mp="3259owmfpyfiu">
          <EmptyState title="Nog geen rapporten" message="Zodra leerlingen oefenen verschijnen hier rapporten." emotion="encouraging" colorScheme="blue" data-id="element-10472" data-mp="373babe0maqxh" />
          
        </div>
      </PageLayout>;
  }
  return <PageLayout title="Rapporten" description={`${summaryStats.activeThisPeriod}/${students.length} leerlingen actief deze ${activePeriodLabel}`} actions={headerActions} data-id="element-10473" data-mp="1cpaez34a0m25">
      
      <div className="max-w-5xl mx-auto space-y-6 pb-12" data-id="element-10474" data-mp="3q1o9zfjsh8dj">
        <div className="flex justify-end mb-[-12px]" data-id="element-10475" data-mp="3l7scee7ftniw">
          <DataFreshness updatedAt={lastLoadedAt} onRefresh={() => setLastLoadedAt(new Date())} data-id="element-10476" data-mp="3h79kkk5johgp" />
          
        </div>

        {/* Insight Bar */}
        <InsightBar message={<>
              De klas heeft een gemiddelde beheersing van{' '}
              {summaryStats.classAvg}%. Er hebben{' '}
              {summaryStats.activeThisPeriod} leerlingen geoefend in de
              afgelopen {activePeriodLabel}.
            </>} data-id="element-10477" data-mp="3c9gdlowf3jfd" />
        

        {/* Period Selector */}
        <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide" data-id="element-10478" data-mp="3ug9aqjtdpnu">
          {PERIODS.map(period => {
          const isActive = periodDays === period.key;
          return <button key={period.key} onClick={() => setPeriodDays(period.key)} className={`relative px-4 py-2 rounded-full text-sm font-bold whitespace-nowrap transition-colors ${isActive ? 'text-white' : 'bg-white border border-slate-200 text-slate-600 hover:bg-indigo-50 hover:text-indigo-700'}`} data-id="element-10479" data-mp="2ok5levt4uxcu">
                
                {isActive && <motion.div layoutId="activePeriodIndicator" className="absolute inset-0 bg-indigo-600 rounded-full shadow-sm" initial={false} transition={{
              type: 'spring',
              stiffness: 500,
              damping: 30
            }} data-id="element-10480" data-mp="38zgcw8onn88m" />}
                <span className="relative z-10" data-id="element-10481" data-mp="3hyjvzcntnnn4">Deze {period.label}</span>
              </button>;
        })}
        </div>

        {/* Week-over-week Vergelijking */}
        {enrichedStudents.length > 0 && <Card className="border-slate-200 shadow-sm overflow-hidden bg-white" data-id="element-10482" data-mp="3rwzmpeixsuw3">
            <div className="px-5 py-4 bg-white border-b border-slate-100 flex items-center justify-between" data-id="element-10483" data-mp="3n099r02fifew">
              <div data-id="element-10484" data-mp="1xl2sxex0vcxv">
                <h2 className="text-lg font-black text-slate-900" data-id="element-10485" data-mp="1xkjlis5006y3">
                  Periode vergelijking ({enrichedStudents.length} leerlingen)
                </h2>
                <p className="text-sm text-slate-500" data-id="element-10486" data-mp="8478rkogaz6o">
                  Deze {activePeriodLabel} vs. vorige {activePeriodLabel}
                </p>
              </div>
            </div>
            {(() => {
          const sorted = [...enrichedStudents].sort((a, b) => b.trendDelta - a.trendDelta);
          return <div className="overflow-x-auto" data-id="element-10487" data-mp="3be6r4kda4ya">
                  <table className="w-full text-left border-collapse min-w-[600px]" data-id="element-10488" data-mp="3a34xaahf79t">
                    <thead data-id="element-10489" data-mp="3lk59o0mt9pj8">
                      <tr className="bg-slate-50 border-b border-slate-200 text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-10490" data-mp="3pje477podrfu">
                        <th className="py-3 px-5" data-id="element-10491" data-mp="3bl68t48aey6s">Leerling</th>
                        <th className="py-3 px-5" data-id="element-10492" data-mp="3gipl3ylfd0w3">Beheersing</th>
                        <th className="py-3 px-5 text-right" data-id="element-10493" data-mp="3gl22oan9t98m">Trend</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100" data-id="element-10494" data-mp="1erl0hmhwt0l9">
                      {sorted.map((student, idx) => {
                  const isHigh = student.avgMastery >= 70;
                  const isMedium = student.avgMastery >= 40 && student.avgMastery < 70;
                  return <tr key={student.id} className="hover:bg-slate-50/50 transition-colors" data-id="element-10495" data-mp="18dfdfr1qvr8u" data-mpi={idx}>
                          
                            <td className="py-3 px-5 font-bold text-sm text-slate-900" data-id="element-10496" data-mp="yd6fo8s3txrc">
                              {student.name}
                            </td>
                            <td className="py-3 px-5" data-id="element-10497" data-mp="1w05byievx3op">
                              <div className="flex items-center gap-3" data-id="element-10498" data-mp="1r1kaygrt6fz7">
                                <span className={`font-black text-sm tabular-nums w-10 ${isHigh ? 'text-emerald-600' : isMedium ? 'text-amber-600' : 'text-rose-600'}`} data-id="element-10499" data-mp="1mb3xwxrwgwdd">
                                
                                  {student.avgMastery}%
                                </span>
                                <div className="w-24 h-1.5 bg-slate-100 rounded-full overflow-hidden hidden sm:block" data-id="element-10500" data-mp="2a0okdqn9ibre">
                                  <div className={`h-full rounded-full ${isHigh ? 'bg-emerald-500' : isMedium ? 'bg-amber-500' : 'bg-rose-500'}`} style={{
                            width: `${student.avgMastery}%`
                          }} data-id="element-10501" data-mp="2i9u83uvggie1" />
                                
                                </div>
                              </div>
                            </td>
                            <td className="py-3 px-5 text-right" data-id="element-10502" data-mp="3186n3x91smhj">
                              <div className="flex justify-end" data-id="element-10503" data-mp="317y7vypgd536">
                                {student.trendDelta > 0 && <div className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 px-2 py-1 rounded-md text-xs font-bold" data-id="element-10504" data-mp="357yw2b2csunf">
                                    <ArrowUpRight className="w-3.5 h-3.5" data-id="element-10505" data-mp="3f04lbd180xyx" />+
                                    {student.trendDelta}%
                                  </div>}
                                {student.trendDelta < 0 && <div className="inline-flex items-center gap-1 bg-rose-50 text-rose-700 px-2 py-1 rounded-md text-xs font-bold" data-id="element-10506" data-mp="3ox9bdny6tdl0">
                                    <ArrowDownRight className="w-3.5 h-3.5" data-id="element-10507" data-mp="phxg80j2hdoq" />
                                    {student.trendDelta}%
                                  </div>}
                                {student.trendDelta === 0 && <div className="inline-flex items-center gap-1 bg-slate-100 text-slate-500 px-2 py-1 rounded-md text-xs font-bold" data-id="element-10508" data-mp="zbnz8pwfcvx1">
                                    <ArrowRight className="w-3.5 h-3.5" data-id="element-10509" data-mp="6k4ayrkz0ro6" />
                                    Stabiel
                                  </div>}
                              </div>
                            </td>
                          </tr>;
                })}
                    </tbody>
                  </table>
                </div>;
        })()}
          </Card>}

        {/* Per-leerling rapport Section */}
        <StudentReportCard students={enrichedStudents} selectedGroup={selectedGroup} getModuleMastery={getModuleMastery} periodDays={periodDays} data-id="element-10510" data-mp="1qzpoxr8371n7" />
        

        {/* Export Section */}
        <div className="space-y-3 pt-4" data-id="element-10511" data-mp="1uif2qlbo44s8">
          <Button onClick={handleExportFullClassReport} size="sm" className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold" data-id="element-10512" data-mp="1ukrkoyhfef5a">
            
            <FileText className="w-4 h-4 mr-2" data-id="element-10513" data-mp="1klaiws5aolel" />
            Volledig Klasrapport Exporteren
          </Button>
          <div className="flex flex-col sm:flex-row gap-3" data-id="element-10514" data-mp="1pkn5dbs97qgn">
            <Button onClick={handleExportClassListCsv} variant="outline" size="sm" className="flex-1 border-slate-200 text-slate-600 hover:bg-slate-50" data-id="element-10515" data-mp="2d9g23agpa2cb">
              
              <Download className="w-4 h-4 mr-2 text-slate-400" data-id="element-10516" data-mp="2iac4tkpbfig5" />
              Klassenlijst CSV
            </Button>

            <Button onClick={handleExportSubjectsCsv} variant="outline" size="sm" className="flex-1 border-slate-200 text-slate-600 hover:bg-slate-50" data-id="element-10517" data-mp="23hiltpau4k6q">
              
              <Download className="w-4 h-4 mr-2 text-slate-400" data-id="element-10518" data-mp="2w6h5wryipweo" />
              Vakken-overzicht CSV
            </Button>
          </div>
        </div>
      </div>
    </PageLayout>;
}